import { FC, useEffect, useState } from "react";
import {
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiSearchInput,
  MiTab,
} from "@miplatform/components";
import { BUTTON_VARIANTS, ENTITY_NAMES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { Colors, IPlanCountResponse } from "@miplatform/types";
import { mdiMagnify, mdiPlus, mdiViewCompactOutline } from "@mdi/js";
import { InputAdornment, useTheme } from "@mui/material";
import { BID_STATUS_DISPLAY } from "../constants/constants";
import { tabToPlanStatusMap } from "../useBidsViewModel";

export const BidsPageHeader: FC<BidsPageHeaderProps> = ({
  isLoading,
  search = "",
  setSearch,
  tabCounts = [],
}) => {
  const { params, navigate } = useRouter();
  const [currentTab, setCurrentTab] = useState(params.tab ?? "search");

  const theme = useTheme();

  useEffect(() => {
    setCurrentTab(params.tab ?? "search");
  }, [params.tab]);

  const handleTabChange = (tab: string) => {
    setCurrentTab((prev) => {
      if (prev === "search") {
        setSearch("");
      }
      return tab;
    });
    if (tab === "search") {
      navigate("/estimating/bids/list");
    } else if (tab === "new") {
      navigate(`/estimating/bids/list/new`);
    } else {
      navigate(`/estimating/bids/list/${tab}`);
    }
  };

  /** Finds the count for a tab within the PlanCountType.Status counts. */
  const getTabCount = (tab: string) =>
    tabCounts.find(
      (count) => count.value === tabToPlanStatusMap[tab].toLowerCase()
    )?.amount ?? null;

  const searchInput =
    currentTab !== "search" ? (
      <MiSearchInput
        value={search}
        handleChange={(e) => setSearch(e.target.value)}
        data-testid="bids-search-input"
        textfieldProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MiIcon
                path={mdiMagnify}
                size={0.8}
                color={theme.palette.text.secondary}
              />
            </InputAdornment>
          ),
        }}
        shrink
      />
    ) : null;

  return (
    <>
      <MiBuildPageHeader
        title={ENTITY_NAMES.BID_REQUESTS}
        titleIcon={
          <MiIcon
            path={mdiViewCompactOutline}
            color={theme.palette.primary.main}
          />
        }
        loading={isLoading}
        searchComponent={searchInput}
        actionComponent={
          <MiButton
            title={`Create ${ENTITY_NAMES.BID_REQUEST}`}
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() => navigate("/estimating/bids/create-bid-request")}
          />
        }
      />
      <MiTab
        tabs={[
          {
            label: "All",
            value: "search",
            onClick: () => navigate("/estimating/bids/list"),
          },
          {
            label: BID_STATUS_DISPLAY.ASSIGNMENT,
            value: "new",
            chipLabel: getTabCount("new"),
            chipColor: Colors.Red,
          },
          {
            label: BID_STATUS_DISPLAY.SETUP,
            value: "setup",
            chipLabel: getTabCount("setup"),
            chipColor: Colors.Red,
          },
          {
            label: BID_STATUS_DISPLAY.PRICING,
            value: "pricing",
            chipLabel: getTabCount("pricing"),
            chipColor: Colors.Red,
          },
          {
            label: BID_STATUS_DISPLAY.APPROVED,
            value: "approved",
          },
        ]}
        currentTab={currentTab}
        onTabChange={handleTabChange}
      />
    </>
  );
};

interface BidsPageHeaderProps {
  isLoading: boolean;
  search?: string;
  setSearch: (value: string) => void;
  tabCounts?: IPlanCountResponse[];
}
